// src/oura/components/aeo/StageChecklist.tsx
//
// What a scan has already finished, and roughly how much longer (#3572).
//
// Shared by the processing toast and the full-page AssessmentProgress. Written
// once because two copies of a progress renderer is how the toast and the page
// end up disagreeing about the same run — which is exactly the class of bug
// #3535 fixed for the stage LABELS, and the same trap is open for the list.
//
// Order comes from the backend array and from nowhere else. `stageLabels.ts`
// maps a name to wording and states plainly that its own order must never be
// read as a sequence: AEO_STAGES has grown twice, and a positional read renames
// every stage after the insertion point while the count stays right.

import { CheckIcon, ClockIcon, Loader2Icon } from "lucide-react";
import { cn } from "../../lib/cn";
import { stageLabel } from "../../stageLabels";
import type { AeoAssessmentEta, AeoAssessmentStage } from "@service/aeo";

/**
 * "about 7 minutes left", or a range when the spread was wide.
 *
 * Returns null whenever we would be guessing. A number the backend has already
 * withdrawn (the run overran it, too few comparable runs) must show as nothing
 * — a timer pinned at "1 minute left" for twenty minutes is worse than no
 * timer, because it is read as a promise.
 */
export function etaText(eta: AeoAssessmentEta | null | undefined): string | null {
    const seconds = eta?.remaining_seconds;
    if (typeof seconds !== "number" || seconds <= 0) return null;
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (eta?.confidence === "wide") {
        // A deliberately loose window. Half to double is honest about a spread
        // the backend has told us it cannot narrow.
        const low = Math.max(1, Math.round(minutes / 2));
        const high = minutes * 2;
        return `about ${low}–${high} minutes left`;
    }
    return minutes === 1 ? "about a minute left" : `about ${minutes} minutes left`;
}

/** "02:14" in the reader's own timezone — a stage finished a moment ago, so the
 *  date would be noise. */
function clock(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export function StageChecklist({
    stages,
    className,
}: {
    stages: AeoAssessmentStage[];
    className?: string;
}) {
    return (
        // Indented behind a hairline rule so the list reads as detail belonging
        // to the row above it, rather than a second list floating in the toast.
        <ul className={cn("my-0 space-y-0.5 border-l border-border py-0.5 pl-3", className)}>
            {stages.map((s) => {
                const done = s.state === "done";
                const active = s.state === "active";
                return (
                    <li
                        key={s.name}
                        className={cn(
                            "flex items-center gap-2 text-xs leading-5",
                            done && "text-muted-foreground",
                            active && "font-medium text-foreground",
                            !done && !active && "text-muted-foreground/60",
                        )}
                    >
                        <span className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
                            {done ? (
                                <CheckIcon className="h-3.5 w-3.5 text-emerald-600/80" />
                            ) : active ? (
                                <Loader2Icon className="h-3 w-3 animate-spin text-blue-600" />
                            ) : (
                                <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                            )}
                        </span>
                        {/* Falls back to a title-cased name, never a blank: a
                            stage the backend ships before anyone writes nicer
                            wording should read slightly rough, not missing. */}
                        <span className="min-w-0 flex-1 truncate">{stageLabel(s.name)}</span>
                        {/* A stage from a run recorded before timings existed is
                            still done — it just has no clock. Absence renders as
                            absence rather than a zero or a dash. */}
                        {/* Fixed width and right-aligned so the column reads as
                            a column: ragged timestamps were most of what made
                            this look untidy. */}
                        {done && s.completed_at && (
                            <span className="w-10 flex-shrink-0 text-right tabular-nums text-muted-foreground/70">
                                {clock(s.completed_at)}
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

/** The estimate on its own line. Renders nothing when there is no honest one. */
export function EtaLine({ eta, className }: { eta: AeoAssessmentEta | null | undefined; className?: string }) {
    const text = etaText(eta);
    if (!text) return null;
    return (
        <span className={cn("inline-flex items-center gap-1 text-xs text-muted-foreground", className)}>
            <ClockIcon className="h-3 w-3" />
            {text}
        </span>
    );
}
